import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Box, Card, Flex, Stack, Text, RingProgress } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/sharp-regular-svg-icons/faArrowRight';

import { emptyObject } from 'ui-components/default-props/empty';

import { ReturnPolicyHeader } from './return-policy-header';
import { SsrConfigIframeButton } from '../../../ssr-config-iframe/ssr-config-iframe';

import { ReturnModeToggle } from './return-mode-toggle';
import type { RetailerConfig } from '../../hooks/use-enhanced-mode-activation';
import { useReturnsTabContext } from '../../../hooks/use-returns-tab-context';
import {
  STORE_ACTIVATION_MODE_ADVANCED,
  BASIC_STORE_ONBOARDING_STEPS,
  ADVANCED_STORE_ONBOARDING_STEPS,
} from '../../../../../../../../store/returns/returns.helpers';

import styles from './styles.module.less';
import { messages } from './messages';
import setupPolicyIllustration from './setup-policy-illustration.svg';

type ReturnPolicyProps = {
  onReload: () => void;
  retailerConfig: RetailerConfig;
};

export const ReturnPolicy = ({ onReload, retailerConfig }: ReturnPolicyProps) => {
  const { storeGuid = '' } = useParams();
  const { formatMessage } = useIntl();
  const { useBrandedReturns, isStoreSetupCompleteForEnhancedReturns } = useReturnsTabContext();

  const onboardingTodosStatus = retailerConfig?.todos ?? (emptyObject as Record<string, boolean>);
  const isAdvancedMode = retailerConfig?.storeProperties?.current_mode === STORE_ACTIVATION_MODE_ADVANCED;
  const steps = isAdvancedMode ? ADVANCED_STORE_ONBOARDING_STEPS : BASIC_STORE_ONBOARDING_STEPS;

  const totalStepsCount = steps.length;
  const completedStepsCount = useMemo(() => {
    return steps.filter((step) => step.every((todo) => onboardingTodosStatus[todo])).length;
  }, [onboardingTodosStatus, steps]);

  const isCompleted = completedStepsCount === totalStepsCount;
  const isInProgress = completedStepsCount > 0 && !isCompleted;
  const completionPercentage = (completedStepsCount / totalStepsCount) * 100;

  if (!useBrandedReturns || !isStoreSetupCompleteForEnhancedReturns) {
    return null;
  }

  return (
    <Card padding='md' radius={6} withBorder>
      <Stack gap='md'>
        <Flex align='center' gap='md' justify='space-between'>
          <ReturnPolicyHeader />
          {isCompleted && (
            <SsrConfigIframeButton storeGuid={storeGuid} theme='secondary'>
              <FormattedMessage {...messages.editPolicyButtonText} />
            </SsrConfigIframeButton>
          )}
          {isInProgress && (
            <Flex align='center' gap='sm' pr={8}>
              <RingProgress sections={[{ value: completionPercentage, color: 'green' }]} size={32} thickness={4} />
              <Text c='gray' size='sm'>
                <FormattedMessage {...messages.completedStepsText} values={{ completedStepsCount, totalStepsCount }} />
              </Text>
            </Flex>
          )}
        </Flex>
        {isCompleted ? (
          <ReturnModeToggle retailerConfig={retailerConfig} storeGuid={storeGuid} />
        ) : (
          <Card padding='md' radius={6} withBorder>
            <Flex gap='md' justify='space-between'>
              <Stack align='flex-start' gap='md'>
                <Stack gap='xs'>
                  <Text size='sm'>
                    <FormattedMessage {...messages.createReturnPolicyTitle} />
                  </Text>
                  <Text c='gray' size='sm'>
                    <FormattedMessage {...messages.createReturnPolicyDescription} />
                  </Text>
                </Stack>
                <SsrConfigIframeButton onModalClosed={onReload} storeGuid={storeGuid} theme={isInProgress ? 'secondary' : 'primary'}>
                  <Flex align='center' gap='sm'>
                    {isInProgress ? (
                      <FormattedMessage {...messages.continuePolicySetupButtonText} />
                    ) : (
                      <FormattedMessage {...messages.setupPolicyButtonText} />
                    )}
                    <FontAwesomeIcon className={styles['arrow-icon']} icon={faArrowRight} />
                  </Flex>
                </SsrConfigIframeButton>
              </Stack>
              <Box>
                <img alt={formatMessage(messages.setupPolicyIllustrationAltText)} src={setupPolicyIllustration} />
              </Box>
            </Flex>
          </Card>
        )}
      </Stack>
    </Card>
  );
};
